/**
 * t187u: clock in / out and breaks through the offline outbox (docs/offline.md §3.4). A punch made with no signal (or
 * whose send hit a network error / 5xx / 429) is saved on the device with the device time of the tap and the location
 * fix taken then, shown straight away (applyPendingClock), and sent when there's signal, strictly in order, each with
 * its op id as the Idempotency-Key and its own client_event_id (so neither a resend nor a lost answer can punch twice).
 * The server records it at the corrected device time and auto-accepts it or flags it for a manager. A refusal (no
 * shift, already clocked in, ...) stops the queue and waits for the person with the reason and the fix; the local
 * clock rolls back by itself because failed ops are never applied. Platform-neutral: storage is the shared outbox store.
 */
import {
  ApiError, CLOCK_OP_KINDS, CLOCK_OP_LABEL, CLOCK_OP_TTL_MS, classifyOutcome, clockOpProblem, isInFlightConflict, outboxRetryDelayMs,
  type ClockOpKind, type GeoReading, type PendingClockOp, type TimeEntry,
} from '@tradeoutlet/shared';
import { dataApi } from './client';
import { createOutboxStore, type KeyValueStore, type OutboxStore } from './unsent';

export interface ClockOpPayload {
  client_event_id: string;
  /** Device time of the tap (ISO). */
  device_time: string;
  /** Clock-out and breaks: the clock-in they belong to, when that clock-in is queued too. */
  clock_in_event_id?: string | null;
  shift_id?: string | null;
  location_id?: string | null;
  geo?: GeoReading | null;
  geo_failure?: 'denied' | 'unavailable' | 'timeout' | 'unsupported' | null;
}
export interface ClockOutboxOp {
  id: string; // = Idempotency-Key
  kind: ClockOpKind;
  userId: string;
  workspaceId: string;
  createdAt: string;
  attempts: number;
  state: 'queued' | 'sending' | 'failed' | 'conflict';
  nextAt?: number;
  error?: string;
  /** What to do about a refusal ("Discard this one; your earlier clock-in stands."). */
  fix?: string;
  payload: ClockOpPayload;
}
export type NewClockOp = Pick<ClockOutboxOp, 'kind' | 'workspaceId' | 'payload'> & { id?: string };

export interface ClockOutboxOptions {
  store: OutboxStore<ClockOutboxOp>;
  uuid: () => string;
  isOnline?: () => boolean;
  now?: () => number;
  rand?: () => number;
  /** A sent op's answer (the entry as the server now has it), or null when it was discarded. */
  onSent?: (op: ClockOutboxOp, entry: TimeEntry | null) => void;
}

export interface ClockOutbox {
  readonly userId: string | null;
  load(userId: string): Promise<void>;
  ops(): ClockOutboxOp[];
  subscribe(fn: () => void): () => void;
  /** Throws when the same punch is already waiting (a double tap). */
  enqueue(op: NewClockOp): Promise<ClockOutboxOp>;
  flush(): Promise<void>;
  retry(id: string): Promise<void>;
  /** Discards the op; a discarded clock-in takes the punches that belong to it with it. */
  discard(id: string): Promise<void>;
  forget(): void;
}

export const clockOutboxStore = (open: (userId: string) => KeyValueStore<ClockOutboxOp>) => createOutboxStore<ClockOutboxOp>(open, { kinds: CLOCK_OP_KINDS, ttlMs: CLOCK_OP_TTL_MS });

/** What the clock overlay needs (shared applyPendingClock). */
export const toPendingClockOp = (o: ClockOutboxOp): PendingClockOp => ({
  id: o.id, kind: o.kind, deviceTime: o.payload.device_time, state: o.state, shiftId: o.payload.shift_id ?? null, locationId: o.payload.location_id ?? null,
});
export const clockOpLabel = (o: ClockOutboxOp) => CLOCK_OP_LABEL[o.kind];

type Outcome = { kind: 'sent'; entry: TimeEntry } | { kind: 'retry'; error: string; delay?: number } | { kind: 'failed' | 'conflict'; error: string; fix: string };

export function clockOutcomeOf(kind: ClockOpKind, e: unknown): Outcome {
  const status = e instanceof ApiError ? e.status : null;
  const msg = e instanceof Error ? e.message : String(e);
  const c = classifyOutcome(status);
  if (c === 'retry') return { kind: 'retry', error: status == null ? 'No signal' : msg };
  if (c === 'conflict' && isInFlightConflict(`${msg} ${JSON.stringify(e instanceof ApiError ? e.body ?? '' : '')}`)) return { kind: 'retry', error: 'Still sending', delay: 2000 };
  const p = clockOpProblem(kind, status, e instanceof ApiError ? e.body : null);
  return { kind: c === 'conflict' ? 'conflict' : 'failed', error: p.message, fix: p.fix };
}

/** The request body for an op (device_time_sent is the moment this send goes out). */
export function clockOpBody(op: ClockOutboxOp, sentAt: string) {
  const p = op.payload;
  const off = { recorded_offline: true, device_time: p.device_time, device_time_sent: sentAt };
  if (op.kind === 'clock.in') {
    return { client_event_id: p.client_event_id, shift_id: p.shift_id ?? undefined, location_id: p.shift_id ? undefined : p.location_id ?? undefined, geo: p.geo ?? null,
      ...(p.geo_failure ? { geo_failure: p.geo_failure } : {}), ...off };
  }
  const base = { client_event_id: p.client_event_id, ...(p.clock_in_event_id ? { clock_in_event_id: p.clock_in_event_id } : {}), ...off };
  return op.kind === 'clock.out' ? { ...base, geo: p.geo ?? null } : base;
}

export function createClockOutbox(o: ClockOutboxOptions): ClockOutbox {
  const now = o.now ?? Date.now;
  const online = o.isOnline ?? (() => true);
  let userId: string | null = null;
  let ops: ClockOutboxOp[] = [];
  let snapshot: ClockOutboxOp[] = [];
  const listeners = new Set<() => void>();
  let flushing: Promise<void> | null = null;
  let again = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const emit = () => { snapshot = ops.map(x => ({ ...x })); listeners.forEach(f => f()); };
  const save = async (op: ClockOutboxOp) => { await o.store.save(op); };
  const drop = async (op: ClockOutboxOp) => { ops = ops.filter(x => x.id !== op.id); await o.store.remove(op.userId, op.id); };
  const ordered = () => [...ops].sort((a, b) => a.payload.device_time.localeCompare(b.payload.device_time) || a.createdAt.localeCompare(b.createdAt));

  const schedule = () => {
    if (timer) { clearTimeout(timer); timer = null; }
    const waits = ops.filter(x => x.state === 'queued' && x.nextAt && x.nextAt > now()).map(x => x.nextAt!);
    if (!waits.length) return;
    timer = setTimeout(() => { timer = null; void api.flush(); }, Math.max(50, Math.min(...waits) - now()));
  };

  async function send(op: ClockOutboxOp): Promise<Outcome> {
    const wf = dataApi().wf;
    const body = clockOpBody(op, new Date(now()).toISOString());
    try {
      const entry = op.kind === 'clock.in' ? await wf.clockIn(op.workspaceId, body as Parameters<typeof wf.clockIn>[1], op.id)
        : op.kind === 'clock.out' ? await wf.clockOut(op.workspaceId, body, op.id)
          : op.kind === 'break.start' ? await wf.startBreak(op.workspaceId, body, op.id) : await wf.endBreak(op.workspaceId, body, op.id);
      return { kind: 'sent', entry };
    } catch (e) {
      return clockOutcomeOf(op.kind, e);
    }
  }

  async function runFlush() {
    if (!online()) return;
    // Clock punches for a person are strictly ordered: the first one that doesn't go stops the rest.
    for (const op of ordered()) {
      if (op.state === 'failed' || op.state === 'conflict' || (op.nextAt && op.nextAt > now())) break;
      op.state = 'sending'; emit();
      const r = await send(op);
      if (r.kind === 'sent') { await drop(op); o.onSent?.(op, r.entry); emit(); continue; }
      op.attempts += 1; op.error = r.error;
      if (r.kind === 'retry') { op.state = 'queued'; op.fix = undefined; op.nextAt = now() + (r.delay ?? outboxRetryDelayMs(op.attempts, (o.rand ?? Math.random)())); }
      else { op.state = r.kind; op.fix = r.fix; op.nextAt = undefined; }
      await save(op); emit();
      break;
    }
  }

  const api: ClockOutbox = {
    get userId() { return userId; },
    async load(u) {
      if (userId === u && ops.length) return;
      userId = u;
      ops = (await o.store.load(u, now())).map(x => (x.state === 'sending' ? { ...x, state: 'queued' as const } : x));
      emit();
      void api.flush();
    },
    ops: () => snapshot,
    subscribe(fn) { listeners.add(fn); return () => { listeners.delete(fn); }; },
    async enqueue(n) {
      if (!userId) throw new Error('Clock outbox: no user loaded');
      const last = ordered().filter(x => x.state !== 'failed' && x.state !== 'conflict').pop();
      if (last && last.kind === n.kind) throw new Error(`${CLOCK_OP_LABEL[n.kind]} is already waiting to send`);
      if (ops.some(x => x.payload.client_event_id === n.payload.client_event_id)) throw new Error(`${CLOCK_OP_LABEL[n.kind]} is already waiting to send`);
      const op: ClockOutboxOp = { ...n, id: n.id || o.uuid(), userId, createdAt: new Date(now()).toISOString(), attempts: 0, state: 'queued' };
      ops = [...ops, op];
      await save(op); emit();
      void api.flush();
      return op;
    },
    flush() {
      if (flushing) { again = true; return flushing; }
      flushing = (async () => {
        try { do { again = false; await runFlush(); } while (again); }
        finally { flushing = null; schedule(); }
      })();
      return flushing;
    },
    async retry(id) {
      const op = ops.find(x => x.id === id);
      if (!op) return;
      op.state = 'queued'; op.nextAt = undefined; op.error = undefined; op.fix = undefined;
      for (const x of ops) if (x.state === 'queued') x.nextAt = undefined;
      await save(op); emit();
      await api.flush();
    },
    async discard(id) {
      const op = ops.find(x => x.id === id);
      if (!op) return;
      const gone = [op, ...(op.kind === 'clock.in' ? ops.filter(x => x !== op && x.payload.clock_in_event_id === op.payload.client_event_id) : [])];
      for (const g of gone) await drop(g);
      emit();
      for (const g of gone) o.onSent?.(g, null);
      void api.flush();
    },
    forget() { if (timer) clearTimeout(timer); timer = null; ops = []; userId = null; emit(); },
  };
  return api;
}
